import React, {useState} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import Toast from 'react-native-toast-message';
import ArrowRight from '../../assets/icons/arrow-right.svg';
import AuthBrand from '../../components/AuthBrand';
import AuthHeader from '../../components/AuthHeader';
import AuthInput from '../../components/AuthInput';
import AuthButton from '../../components/AuthButton';
import {colors, fonts, radii} from '../../config/theme';
import {requestPasswordReset} from '../../services/auth-service';
import {isValidEmail} from '../../utility/validation';

// Step 1 of the reset: ask for the email and send a 6-digit code to it.
// VerifyOtp (mode 'reset') checks the code, then UpdatePassword sets the
// new password.
const ForgotPassword = ({navigation, route}) => {
  const {t} = useTranslation();
  const insets = useSafeAreaInsets();
  const [email, setEmail] = useState(route.params?.email ?? '');
  const [error, setError] = useState();
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    const address = email.trim();
    if (!address) {
      setError(t('root.Auth.emailRequired'));
      return;
    }
    if (!isValidEmail(address)) {
      setError(t('root.Auth.emailInvalid'));
      return;
    }
    setSending(true);
    try {
      await requestPasswordReset(address);
      navigation.navigate('VerifyOtp', {mode: 'reset', email: address});
    } catch (err) {
      console.log('requestPasswordReset error', err);
      Toast.show({
        type: 'error',
        text1: t('root.ForgotPassword.sendFailed'),
        text2: err?.message,
      });
    } finally {
      setSending(false);
    }
  };

  const backToLogin = () => navigation.goBack();

  return (
    <View style={styles.screen}>
      <AuthHeader title={t('root.ForgotPassword.title')} onBack={backToLogin} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            styles.scrollContent,
            {paddingBottom: insets.bottom + 16},
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <AuthBrand glowId="forgotGlow" />

          <View style={styles.card}>
            <Text style={styles.heading} accessibilityRole="header">
              {t('root.ForgotPassword.heading')}
            </Text>
            <Text style={styles.copy}>{t('root.ForgotPassword.body')}</Text>
            <View style={styles.form}>
              <AuthInput
                variant="filled"
                required
                label={t('root.ForgotPassword.email')}
                placeholder={t('root.ForgotPassword.emailPlaceholder')}
                value={email}
                onChangeText={value => {
                  setEmail(value);
                  if (error) {
                    setError(undefined);
                  }
                }}
                error={error}
                keyboardType="email-address"
                autoCapitalize="none"
                autoCorrect={false}
                autoComplete="email"
                textContentType="username"
                returnKeyType="send"
                onSubmitEditing={handleSubmit}
              />
              <AuthButton
                variant="primary"
                title={t('root.ForgotPassword.submit')}
                iconRight={
                  <ArrowRight width={16} height={16} color={colors.onPrimary} />
                }
                loading={sending}
                onPress={handleSubmit}
              />
            </View>
          </View>

          <Pressable
            onPress={backToLogin}
            hitSlop={8}
            accessibilityRole="link"
            style={({pressed}) => [styles.footer, pressed && styles.linkPressed]}>
            <Text style={styles.muted}>
              {t('root.ForgotPassword.rememberPassword')}{' '}
              <Text style={[styles.link, styles.linkUnderlined]}>
                {t('root.ForgotPassword.backToLogin')}
              </Text>
            </Text>
          </Pressable>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
  flex: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 20,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radii.sheet,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
    padding: 24,
    ...Platform.select({
      ios: {
        shadowColor: colors.primary,
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.07,
        shadowRadius: 18,
      },
      // RN 0.77 draws square elevation under big radii (see theme.js), so
      // Android leans on a firmer border instead.
      default: {borderColor: colors.border},
    }),
  },
  heading: {
    fontFamily: fonts.bold,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.3,
    color: colors.text,
    textAlign: 'center',
  },
  copy: {
    fontFamily: fonts.regular,
    fontSize: 13.5,
    lineHeight: 21,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: 10,
    marginBottom: 24,
    paddingHorizontal: 4,
  },
  form: {
    gap: 20,
  },
  footer: {
    marginTop: 'auto',
    paddingTop: 32,
    alignSelf: 'center',
  },
  muted: {
    fontFamily: fonts.regular,
    fontSize: 14,
    lineHeight: 20,
    color: colors.textMuted,
  },
  link: {
    fontFamily: fonts.bold,
    fontSize: 14,
    lineHeight: 20,
    color: colors.primary,
  },
  linkUnderlined: {
    textDecorationLine: 'underline',
  },
  linkPressed: {
    opacity: 0.6,
  },
});

export default ForgotPassword;
